<template>
  <div class="app-pagination-wrapper mt-4 lr" v-if="shouldShowPagination">
    <v-row
      justify="center"
      align="center"
      class="mt-7 mx-4 app-pagination-row"
      no-gutters
    >
      <v-col cols="12">
        <div class="app-pagination__content">
          <div class="app-pagination__controls">
            <div v-show="totalPages > 0" class="pagination">
              <button :disabled="currentPage == 1" @click="controls(-1)" class="buttonText">
                <v-icon v-if="$vuetify.breakpoint.smAndDown">mdi-chevron-left</v-icon>
                <span v-if="$vuetify.breakpoint.mdAndUp">Anterior</span>
              </button>
              <button
                  v-for="(page, index) in pagesToShow"
                  :key="index"
                  @click="changePage(page)"
                  :class="{ active: page === value }"
                  class="buttonPage"
              >
                {{ page }}
              </button>
              <button :disabled="currentPage === totalPages" @click="controls(1)" class="buttonText">
                <v-icon v-if="$vuetify.breakpoint.smAndDown">mdi-chevron-right</v-icon>
                <span v-if="$vuetify.breakpoint.mdAndUp">Siguiente</span>
              </button>
            </div>
            <v-select
              v-if="hasData && showPerPageSelector"
              class="app-pagination__select ml-3"
              :items="paginateByOptions"
              v-model="perPageModel"
              @change="updatePerPage"
              dense
              hide-details
              outlined
              color="bluePrimary"
              :menu-props="{ offsetY: true, contentClass: 'app-pagination__menu' }"
            ></v-select>
          </div>
          <p v-if="disabledSelectTotal && hasData" class="app-pagination__total mb-0">
            Total registros: {{ totalRegistros }}
          </p>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: "AppPagination",
  props: {
    value: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    totalRegistros: {
      type: Number,
      default: 0,
    },
    perPage: {
      type: Number,
      default: 10,
    },
    paginateBy: {
      type: Array,
      default: () => [5, 10, 20, 50, 100],
    },
    disabledSelectTotal: {
      type: Boolean,
      default: false,
    },
    showPerPageSelector: {
      type: Boolean,
      default: false,
    }
  },
  emits: ["update:modelValue", "input", "page-changed", "change", "update:perPage"],
  data: () => {
    return {
      totalVisible: 3,
      localItemsPerPage: null,
    };
  },
  computed: {
    currentPage: {
      get() {
        return this.value;
      },
      set(newPage) {
        this.$emit("input", newPage);
      },
    },
    perPageModel: {
      get() {
        return this.localItemsPerPage !== null ? this.localItemsPerPage : this.perPage;
      },
      set(value) {
        if (value !== this.perPage) {
          this.localItemsPerPage = null;
        } else {
          this.localItemsPerPage = value;
        }
        this.$emit("update:perPage", value);
      },
    },
    paginateByOptions() {
      const options = Array.isArray(this.paginateBy) ? [...this.paginateBy] : [];
      if (!options.includes(this.perPage)) {
        options.push(this.perPage);
      }
      return options
        .filter((option) => typeof option === "number" && option > 0)
        .sort((a, b) => a - b);
    },
    hasData() {
      return Number(this.totalRegistros) > 0 || this.totalPages > 0;
    },
    shouldShowPagination() {
      // Mostrar si hay páginas O si hay registros válidos
      const hasValidTotalPages = this.totalPages > 0;
      const hasValidTotalRegistros = this.totalRegistros !== null && 
                                      this.totalRegistros !== undefined && 
                                      !isNaN(this.totalRegistros) &&
                                      this.totalRegistros > 0;
      
      return hasValidTotalPages || hasValidTotalRegistros;
    },
    pagesToShow() {
      const currentPage = this.value;
      const total = this.totalPages;
      const totalVisible = this.totalVisible;
      const halfVisible = Math.floor(totalVisible / 2);
      let from = currentPage - halfVisible;
      let to = currentPage + halfVisible;

      if (from < 1) {
        from = 1;
        to = Math.min(totalVisible, total);
      }
      if (to > total) {
        to = total;
        from = Math.max(1, total - totalVisible + 1);
      }

      const pages = [];
      if (from > 1) {
        pages.push(1);
        if (from > 2) {
          pages.push("...");
        }
      }
      for (let i = from; i <= to; i++) {
        pages.push(i);
      }
      if (to < total) {
        if (to < total - 1) {
          pages.push("...");
        }
        pages.push(total);
      }
      return pages;
    },
  },
  watch: {
    value(newValue, oldValue) {
      this.$emit("change", newValue, oldValue);
    },
  },
  methods: {
    changePage(page) {
      if (page === "...") return;
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.$emit("page-changed", page);
      }
    },
    controls(variable) {
      const newPage = this.currentPage + variable;
      if (newPage >= 1 && newPage <= this.totalPages) {
        this.currentPage = newPage;
        this.$emit("page-changed", newPage);
      }
    },
    updatePerPage() {
      if (this.value > 1) {
        this.currentPage = 1;
      }
      this.$emit("change");
    },
  },
};
</script>

<style lang="scss" scoped>
.pagination {
  display: flex !important;
  justify-content: center;
  align-items: center;
  padding: 10px;
}

.pagination, .buttonText, .buttonPage {
  color: black;
  border: none;
  cursor: pointer;
  padding: 3px 10px;
  margin: 0 5px;
}

.buttonText {
  background-color: transparent;
}

.buttonText:hover .v-icon.v-icon {
  background-color: #1C1E4D;
  color: #fff !important;
}

.v-icon {
  transition: none !important;
}

.buttonPage {
  background: #ffffff;
  border-radius: 15px;
  max-width: 23px;
  text-align: center;
  display: flex;
  justify-content: center;
  box-shadow: 0 2px 2px rgb(213, 211, 211);
}

.pagination button:hover {
  background-color: #1C1E4D;
  color: #fff;
  border-radius: 10px;
}

.pagination button.active {
  background-color: #1C1E4D;
  border-radius: 15px;
  max-width: 23px;
  text-align: center;
  display: flex;
  justify-content: center;
  color: #fff;
  cursor: default;
}

.pagination button:disabled {
  pointer-events: none;
  opacity: 0.6;
}

::v-deep .v-pagination__item {
  border-radius: 10px !important;
}

::deep(.v-select__selection--comma) {
  margin: 7px 4px 7px 0;
  min-height: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #313945 !important;
}

::deep(.theme--light.v-pagination .v-pagination__item--active) {
  color: #ffffff;
  background-color: #313945;
}

.app-pagination-row {
  row-gap: 12px;
}

.app-pagination__content {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
}

.app-pagination__controls {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
}

.app-pagination__select {
  max-width: 85px !important;
}

::v-deep .app-pagination__select .v-input__control {
  min-height: 44px;
}

::v-deep .app-pagination__select .v-input__slot {
  border-radius: 10px !important;
  padding: 0 10px !important;
  box-shadow: none !important;
  background-color: #ffffff !important;
  transition: border-color 0.2s ease;
}

::v-deep .app-pagination__select.v-text-field--outlined fieldset {
  border: 2px solid #d0d0d0 !important;
}

::v-deep .app-pagination__select.v-text-field--outlined:hover fieldset {
  border: 2px solid #b0b0b0 !important;
}

::v-deep .app-pagination__select.v-text-field--outlined.v-input--is-focused fieldset {
  border: 2px solid #1C1E4D !important;
}

::v-deep .app-pagination__select.v-input--is-focused fieldset {
  border: 2px solid #1C1E4D !important;
}

::v-deep .app-pagination__select .v-input__slot::before,
::v-deep .app-pagination__select .v-input__slot::after {
  border: none !important;
}

::v-deep .app-pagination__select .v-select__selections {
  flex-wrap: nowrap;
}

::v-deep .app-pagination__select .v-input__append-inner .v-icon {
  color: #313945 !important;
}

::v-deep .app-pagination__menu {
  border: 2px solid #313945;
  border-radius: 12px;
  box-shadow: 0px 8px 20px rgba(16, 24, 40, 0.12);
}

::v-deep .app-pagination__menu .v-list-item {
  min-height: 36px;
}

.app-pagination__total {
  color: #313945;
  font-size: 14px;
  position: absolute;
  right: 0;
}

@media (max-width: 600px) {
  .app-pagination__content {
    flex-direction: column;
  }

  .app-pagination__total {
    position: static;
  }
}
</style>
